import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, ArrowRight } from "lucide-react";
import { ModeIcon, formatMoney } from "@/components/ventrix/atoms";
import Pagination from "@/components/ventrix/Pagination";

const LIMIT = 50;
const blank = () => ({
  lane_origin: "", lane_destination: "", mode: "air", weight_break_kg: 0,
  base_rate: 100, per_kg: 2, per_cbm: 0, currency: "USD",
  fuel_pct: 14, security_pct: 3, remote_area_pct: 0, active: true,
});

export default function RateCards() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(blank());

  const load = () => {
    setItems(null);
    api.get(`/v1/rate-cards?limit=${LIMIT}&offset=${offset}`)
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total ?? (data.items || []).length); })
      .catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [offset]); // eslint-disable-line

  const startCreate = () => { setEditing(null); setF(blank()); setOpen(true); };
  const startEdit = (r) => { setEditing(r); setF({...blank(), ...r}); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...f,
        weight_break_kg: Number(f.weight_break_kg)||0, base_rate: Number(f.base_rate)||0,
        per_kg: Number(f.per_kg)||0, per_cbm: Number(f.per_cbm)||0,
        fuel_pct: Number(f.fuel_pct)||0, security_pct: Number(f.security_pct)||0,
        remote_area_pct: Number(f.remote_area_pct)||0,
      };
      if (editing) await api.patch(`/v1/rate-cards/${editing.id}`, payload);
      else await api.post("/v1/rate-cards", payload);
      setOpen(false); load(); toast.success("Rate card saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (r) => {
    if (!window.confirm("Delete this rate card?")) return;
    try { await api.delete(`/v1/rate-cards/${r.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <PortalLayout section="ops">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="vtx-eyebrow">/ Ops · Rates</div>
          <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Lane-based rate cards</h1>
        </div>
        <button className="vtx-btn-primary inline-flex items-center gap-2" onClick={startCreate} data-testid="new-rate-card"><Plus size={14}/> New rate card</button>
      </div>

      <div className="vtx-card">
        {items === null && <div className="p-6 space-y-2">{[1,2,3].map(i=><div key={i} className="vtx-skeleton h-12" />)}</div>}
        {items && (
          <div className="overflow-x-auto">
            <table className="vtx-table w-full">
              <thead><tr>
                <th>Lane</th><th>Mode</th><th>Base</th><th>Per kg</th><th>Per CBM</th><th>Surcharges</th><th>Active</th><th></th>
              </tr></thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan="8" className="text-center text-sm text-[#475569] py-10">No rate cards yet.</td></tr>}
                {items.map(r => (
                  <tr key={r.id} data-testid={`rate-row-${r.id}`}>
                    <td><span className="font-semibold">{r.lane_origin}</span> <ArrowRight size={11} className="inline text-[#A9C0E0]" /> <span className="font-semibold">{r.lane_destination}</span></td>
                    <td><ModeIcon mode={r.mode} showLabel /></td>
                    <td className="font-mono">{formatMoney(r.base_rate, r.currency)}</td>
                    <td className="font-mono">{r.per_kg}</td>
                    <td className="font-mono">{r.per_cbm}</td>
                    <td className="font-mono text-xs">F {r.fuel_pct}% · S {r.security_pct}%</td>
                    <td>{r.active ? <span className="text-[#166534] font-bold text-xs">ACTIVE</span> : <span className="text-[#475569] text-xs">Off</span>}</td>
                    <td className="flex gap-2">
                      <button onClick={()=>startEdit(r)} data-testid={`edit-rate-${r.id}`} className="text-[#0E2F76] hover:text-[#d41f26]"><Pencil size={14}/></button>
                      <button onClick={()=>del(r)} data-testid={`del-rate-${r.id}`} className="text-[#d41f26]"><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} rate card</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <label><span className="vtx-eyebrow block mb-1.5">Origin lane</span><input className="vtx-input" value={f.lane_origin} onChange={(e)=>setF({...f, lane_origin: e.target.value})} data-testid="rc-origin"/></label>
            <label><span className="vtx-eyebrow block mb-1.5">Destination lane</span><input className="vtx-input" value={f.lane_destination} onChange={(e)=>setF({...f, lane_destination: e.target.value})} data-testid="rc-dest"/></label>
            <label><span className="vtx-eyebrow block mb-1.5">Mode</span>
              <select className="vtx-input" value={f.mode} onChange={(e)=>setF({...f, mode: e.target.value})} data-testid="rc-mode">
                <option value="air">Air</option><option value="sea">Sea</option><option value="road">Road</option>
              </select>
            </label>
            <label><span className="vtx-eyebrow block mb-1.5">Weight break (kg)</span><input type="number" className="vtx-input" value={f.weight_break_kg} onChange={(e)=>setF({...f, weight_break_kg: e.target.value})}/></label>
            <label><span className="vtx-eyebrow block mb-1.5">Base rate</span><input type="number" step="0.01" className="vtx-input" value={f.base_rate} onChange={(e)=>setF({...f, base_rate: e.target.value})}/></label>
            <label><span className="vtx-eyebrow block mb-1.5">Per kg</span><input type="number" step="0.01" className="vtx-input" value={f.per_kg} onChange={(e)=>setF({...f, per_kg: e.target.value})}/></label>
            <label><span className="vtx-eyebrow block mb-1.5">Per CBM</span><input type="number" step="0.01" className="vtx-input" value={f.per_cbm} onChange={(e)=>setF({...f, per_cbm: e.target.value})}/></label>
            <label><span className="vtx-eyebrow block mb-1.5">Currency</span><input className="vtx-input" value={f.currency} onChange={(e)=>setF({...f, currency: e.target.value})}/></label>
            <label><span className="vtx-eyebrow block mb-1.5">Fuel %</span><input type="number" step="0.01" className="vtx-input" value={f.fuel_pct} onChange={(e)=>setF({...f, fuel_pct: e.target.value})}/></label>
            <label><span className="vtx-eyebrow block mb-1.5">Security %</span><input type="number" step="0.01" className="vtx-input" value={f.security_pct} onChange={(e)=>setF({...f, security_pct: e.target.value})}/></label>
            <label><span className="vtx-eyebrow block mb-1.5">Remote area %</span><input type="number" step="0.01" className="vtx-input" value={f.remote_area_pct} onChange={(e)=>setF({...f, remote_area_pct: e.target.value})}/></label>
            <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={f.active} onChange={(e)=>setF({...f, active: e.target.checked})}/> <span className="text-sm font-semibold">Active</span></label>
          </div>
          <DialogFooter>
            <button className="vtx-btn-ghost" onClick={()=>setOpen(false)}>Cancel</button>
            <button className="vtx-btn-primary" onClick={save} data-testid="save-rate">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}
